import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2, Volume2 } from "lucide-react";
import { useLang } from "@/lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_LABELS = { pt: "Português", en: "English", es: "Español" };

/** Audio narration player. Streams MP3 from /api/ai/tts/case/{slug}?lang={lang}. */
export default function CaseAudioPlayer({ slug, defaultLang = "pt" }) {
  const [lang, setLang] = useState(defaultLang);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");
  const audioRef = useRef(null);
  const { t } = useLang();
  const Ci = t.case || {};

  // Reset and reload when lang/slug changes. Setters and refs are stable — no additional deps needed.
  useEffect(() => {
    setPlaying(false); setProgress(0); setDuration(0); setError("");
    const a = audioRef.current;
    if (a) { a.pause(); a.removeAttribute("src"); a.load(); }
  }, [slug, lang]);

  const ensureSrc = () => {
    const a = audioRef.current;
    if (!a) return null;
    if (!a.src) {
      a.src = `${API}/ai/tts/case/${slug}?lang=${lang}`;
    }
    return a;
  };

  const toggle = async () => {
    const a = ensureSrc(); if (!a) return;
    if (a.paused) {
      try {
        setError("");
        if (!a.duration) setLoading(true);
        await a.play();
        setPlaying(true);
      } catch (e) {
        // Autoplay may reject when the source is still buffering. If it's a
        // NotSupportedError or NotAllowedError we only surface the error when
        // the audio itself signals a real load failure (onError handler).
        // Otherwise we stay in the loading state — Chrome will trigger onError
        // if the server returns a 5xx.
        if (e?.name === "NotAllowedError") {
          setError("Toque no botão de play novamente para iniciar a narração.");
          setLoading(false);
        }
      }
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const seek = (e) => {
    const a = audioRef.current; if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * a.duration;
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div className="bg-black text-white p-5 md:p-6 rounded-md border border-white/10" data-testid="case-audio-player">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent-yellow">
          <Volume2 className="w-3.5 h-3.5" /> {Ci.listen || "Ouça este case"}
        </div>
        <div className="flex gap-1">
          {Object.entries(LANG_LABELS).map(([k, l]) => (
            <button key={k} onClick={() => setLang(k)} data-testid={`audio-lang-${k}`} className={`text-[10px] px-2 py-0.5 rounded transition ${lang === k ? "bg-accent-yellow text-black font-semibold" : "border border-white/15 text-neutral-300 hover:border-white/40"}`}>{k.toUpperCase()}</button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={toggle} disabled={loading && !duration} data-testid="audio-toggle" className="w-12 h-12 rounded-full bg-accent-yellow text-black flex items-center justify-center hover:bg-accent-green transition disabled:opacity-60">
          {loading && !duration ? <Loader2 className="w-5 h-5 animate-spin" /> : playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
        </button>
        <div className="flex-1">
          <div className="text-xs text-neutral-400 mb-1.5">
            {loading && !duration
              ? "Gerando narração… pode levar até 1 minuto na primeira vez."
              : (Ci.listenSub || "Narração em {lang} · voz feminina").replace("{lang}", LANG_LABELS[lang])}
          </div>
          <div onClick={seek} className="h-2 bg-white/10 rounded-full cursor-pointer overflow-hidden" data-testid="audio-bar">
            <div className="h-full bg-accent-yellow transition-[width] duration-150" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-neutral-500 mt-1 font-mono">
            <span>{fmt(progress)}</span><span>{fmt(duration)}</span>
          </div>
        </div>
      </div>

      {error && <div className="mt-2 text-xs text-red-300">{error}</div>}

      <audio
        ref={audioRef}
        preload="none"
        onLoadedMetadata={(e) => { setDuration(e.currentTarget.duration); setLoading(false); }}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onWaiting={() => setLoading(true)}
        onPlaying={() => setLoading(false)}
        onError={() => { setLoading(false); setError("Não conseguimos gerar a narração agora. Tente novamente."); }}
      />
    </div>
  );
}
